import React, { FC, PropsWithChildren } from 'react';

import { AttachmentMode, AttachmentType } from '../../../models/attachment';

import ButtonAttachmentDelete from './ButtonAttachmentDelete';

import { getFileIconColor, getIsUploading } from './helpers';
import * as Styled from './styled';

export type Props = PropsWithChildren & {
  attachment: AttachmentType;
  canDeleteFile: boolean;
  isDisabled?: boolean;
  mode: AttachmentMode;
  requestFileRemove: () => void;
};

const ICON_HEIGHT = 30;
const ICON_WIDTH = 27;

/**
 * Component that renders single attachment for draft message
 *
 * @param props                   Props passed to the component
 * @param props.attachment        File that is attached
 * @param props.canDeleteFile     Whether user has permission to delete attachment
 * @param props.children          Children of the component
 * @param props.isDisabled        Whether the chip is disabled
 * @param props.mode              In which mode to render the component
 * @param props.requestFileRemove Callback for removing file from the drafts array or message
 * @returns                       The component itself
 */
const AttachmentChip: FC<Props> = ({
  attachment,
  canDeleteFile,
  children,
  isDisabled = false,
  mode,
  requestFileRemove,
}) => {
  const { extension } = attachment;
  const iconColor = getFileIconColor(extension);
  const isUploading = getIsUploading(attachment);

  return (
    <Styled.Chip
      data-mode={mode}
      deleteIcon={
        <ButtonAttachmentDelete
          isUploading={isUploading}
          mode={mode}
          requestButtonClick={requestFileRemove}
        />
      }
      disabled={isDisabled}
      icon={
        <Styled.Icon
          color={iconColor}
          height={ICON_HEIGHT}
          text={extension}
          width={ICON_WIDTH}
        />
      }
      label={children}
      onDelete={canDeleteFile ? requestFileRemove : undefined}
      variant="outlined"
    />
  );
};

export default AttachmentChip;
